import React                from 'react';
import Popover              from '@widesk-ui/components/Popover';
import View                 from '@widesk-ui/components/View';
import Tooltip              from '@widesk-ui/components/Tooltip';
import { BgColorsOutlined } from '@ant-design/icons';
import { CloseOutlined }    from '@ant-design/icons';
import useDesignTokens      from '@/hooks/useDesignTokens';

export function SettingsPopover({ title, open, onOpenChange, width = 260, content, children }: {
	title: React.ReactNode;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	width?: number;
	content: React.ReactNode;
	children: React.ReactElement;
}) {
	const tokens = useDesignTokens();

	return (
		<Popover
			trigger="click"
			placement="rightTop"
			open={open}
			onOpenChange={onOpenChange}
			autoAdjustOverflow={{ shiftY: true }}
			content={(
				<View gap={10} style={{ width }}>
					<View row spread centerV style={{ margin: '-2px -12px 0', padding: '0 8px 10px 12px', borderBottom: `1px solid ${tokens.border.subtle}` }}>
						<View row centerV gap={4} size={15} style={{ fontWeight: 600 }}>{title}</View>
						<button
							type="button"
							aria-label="Fermer"
							onClick={() => onOpenChange(false)}
							style={{ padding: 4, border: 'none', background: 'none', cursor: 'pointer', color: tokens.text.muted, display: 'flex' }}
						>
							<CloseOutlined style={{ fontSize: 12 }} />
						</button>
					</View>

					<View gap={12}>{content}</View>
				</View>
			)}
		>
			{children}
		</Popover>
	);
}

export default function SettingsButton({ label, title = label, summary, details = [], preview, mixed, themed, width, unavailable, children }: {
	label: string;
	title?: React.ReactNode;
	summary: string;
	/** Réglages secondaires modifiés : ils passent dans l'infobulle et allument le point. */
	details?: string[];
	preview?: React.ReactNode;
	mixed?: boolean;
	themed?: boolean;
	width?: number;
	/** Raison affichée en infobulle : le bouton reste visible mais grisé, sans popin. */
	unavailable?: string;
	children?: React.ReactNode;
}) {
	const tokens = useDesignTokens();
	const [open, setOpen] = React.useState(false);

	const button = (
		<button
			type="button"
			aria-label={`${label} : ${unavailable ?? [summary, ...details].join(', ')}`}
			aria-disabled={!!unavailable}
			aria-expanded={unavailable ? undefined : open}
			title={unavailable ? undefined : [summary, ...details].join(' · ')}
			style={{
				position: 'relative',
				width: '100%',
				minWidth: 0,
				height: 52,
				padding: '0 12px',
				borderRadius: 8,
				cursor: unavailable ? 'not-allowed' : 'pointer',
				opacity: unavailable ? 0.55 : 1,
				display: 'inline-flex',
				alignItems: 'center',
				gap: 10,
				textAlign: 'left',
				color: tokens.text.default,
				border: `1px ${mixed && !unavailable ? 'dashed' : 'solid'} ${!unavailable && (mixed || open) ? tokens.color.primary : tokens.border.default}`,
				background: tokens.surface.elevated,
			}}
		>
			{preview}
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 14 }}>{label}</span>
				<span style={{ fontSize: 12, color: tokens.text.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</span>
			</span>
			{themed && !unavailable && <BgColorsOutlined style={{ color: tokens.color.primary, fontSize: 12 }} />}
			{!!details.length && !unavailable && (
				<span
					style={{
						position: 'absolute',
						top: 5,
						right: 5,
						width: 6,
						height: 6,
						borderRadius: '50%',
						background: tokens.color.primary,
					}}
				/>
			)}
		</button>
	);

	if (unavailable) return <Tooltip title={unavailable}>{button}</Tooltip>;

	return (
		<SettingsPopover title={title} open={open} onOpenChange={setOpen} width={width} content={children}>
			{button}
		</SettingsPopover>
	);
}
